import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ContextActionType, useDumbActionsContext } from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FilterOptionValue,
    FiltersDatatableList,
} from '@europrocurement/flexy-datatable';
import { ArticlesJsonldArticleRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { useSelector } from 'react-redux';
import { addItem, FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    ArticleCategorySelector,
    domainSelector,
    useOffreService,
} from '@europrocurement/l2d-domain';
import { formatOptionsByKeys } from '../forms/options';
import { displayCustomValue, getPriceColumn, getStringColumn } from '../listings';
import RoutedListing from '../components/RoutedListing';
import { getStaticSelectFilter } from '../components/filters';
import { PossibleTypes } from '../../Societes';
import { articleCreatePath, articleFichePath } from './routes';

const Panel: React.FunctionComponent = function () {
    const navigate = useNavigate();

    const { articleModel: model } = useOffreService();
    const fetchedDomainsOptions = useSelector(domainSelector).main.data;
    const domainsOptions = formatOptionsByKeys(fetchedDomainsOptions, 'id', 'libelle');
    const fetchedCategoriesOptions = useSelector(ArticleCategorySelector).main.data;
    const categoriesOptions: FilterOptionValue[] = formatOptionsByKeys(
        fetchedCategoriesOptions,
        'id',
        'libelle',
    ).map((option) => ({ ...option, value: option.value?.toString() ?? '' }));

    const goToCreate = React.useCallback(() => navigate(articleCreatePath), [navigate]);
    const goToFiche = React.useCallback(
        (item: { id: string | number }) => navigate(articleFichePath(item.id)),
        [navigate],
    );

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer un nouvel article',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: goToCreate,
            },
        ],
        [goToCreate],
    );

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<ArticlesJsonldArticleRead>[] = [
        displayCustomValue('domaine', (domaine) => domaine.libelle, 'Domaine'),
        getStringColumn('libelle', 'Libellé'),
        getStringColumn('codeArticle'),
        getStringColumn('rubArticle', 'Rubrique'),
        getPriceColumn('prixUnitaire'),
    ];

    // Force string value as defined in FilterOptionValue
    const domainsSelectInputOptions: FilterOptionValue[] = domainsOptions.map((option) => ({
        ...option,
        value: option.value?.toString() ?? '',
    }));

    const filters: FiltersDatatableList = [
        {
            label: 'Domaine',
            field: 'domaineId',
            type: 'staticSelect',
            options: domainsSelectInputOptions,
            renderFilterList: (value) => {
                const selectedOption = domainsOptions.find(
                    (option) => option.value === value.toString(),
                );
                return selectedOption ? selectedOption.label : 'Aucun';
            },
        },
        getStaticSelectFilter("Catégorie d'article", 'catArticleId', categoriesOptions),
        {
            label: 'Code article',
            field: 'codeArticle',
            type: 'text',
        },
        {
            label: 'Article supprimé',
            field: 'deleted',
            type: 'boolean',
        },
    ];

    return (
        <RoutedListing
            listingTitle="Liste des articles"
            createButton={{
                label: 'Créer un nouvel article',
                icon: addItem,
                onClick: goToCreate,
            }}
            listingColumns={columns as ColumnDatatable<unknown>[]}
            model={model}
            filters={filters}
            filtersControl
            searchBarType={PossibleTypes.Article}
            onRowClick={goToFiche}
        />
    );
};

export default Panel;
